import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';
import useInterval from '@hooks/useInterval';
import { I18n, Translate } from '@utils/i18n';
import { ReactComponent as InfoIcon } from './info.svg';
import { Logger } from '_clean-code/utils/logger';
import { sessionStore } from '_clean-code/utils/storage';

interface VersionData {
  version: string;
  git: string;
  tag: string;
}

// Env vars arrive as strings, so `VITE_APP_REFRESH=false` is the truthy string
// "false" — comparing rather than coercing is what makes the flag switchable.
const CHECK_FOR_REFRESH =
  (import.meta.env.VITE_APP_REFRESH ?? 'true') !== 'false';
const CHECK_FOR_REFRESH_INTERVAL = CHECK_FOR_REFRESH ? 1 * 60 * 1000 : null;
const COUNTDOWN_SECONDS = 5 * 60; // 5 minutes in seconds

/**
 * The version we last reloaded for. In sessionStorage so it survives the reload
 * it guards but dies with the tab, which is exactly the lifetime we want.
 *
 * Without it, a `/version.json` that keeps reporting a version the reload never
 * produces — origins serving mismatched builds, a stale edge cache — reloads the
 * page forever. TV screens hit this hardest: they reload unattended, and we saw
 * one do it 1440 times a day for a week.
 */
const RELOADED_FOR_VERSION_KEY = 'PULSAR#RELOADED_FOR_VERSION';

const hasAlreadyReloadedFor = (version: string) =>
  sessionStore.read(RELOADED_FOR_VERSION_KEY) === version;

const reloadForVersion = (version: string) => {
  sessionStore.write(RELOADED_FOR_VERSION_KEY, version);
  window.location.reload();
};

interface RefreshPageCheckerProps {
  classNames?: string;
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
};

function RefreshPageChecker(props: RefreshPageCheckerProps) {
  const [version, setVersion] = useState<VersionData | undefined>(undefined);
  const [showRefresh, setShowRefresh] = useState<boolean>(false);
  const [wasDismissed] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(COUNTDOWN_SECONDS);
  // The version the banner is offering to upgrade to, recorded when we act on
  // it so the same loop guard covers the banner path.
  const [pendingVersion, setPendingVersion] = useState<string | undefined>();

  const check = async () => {
    try {
      if (showRefresh) return;
      Logger.debug('RefreshPageChecker::check: Checking for new version');
      const response = await fetch('/version.json', { cache: 'no-store' });

      // Check if the response is successful
      if (!response.ok) {
        Logger.error(
          `RefreshPageChecker::check: HTTP error! status: ${response.status}`
        );
        return;
      }

      // Check if the response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        Logger.error(
          'RefreshPageChecker::check',
          `Expected JSON but got: ${contentType}`
        );
        return;
      }

      const data = (await response.json()) as VersionData;

      if (!version) {
        setVersion(data);
        return;
      }

      if (data.version !== version.version) {
        if (hasAlreadyReloadedFor(data.version)) {
          // We reloaded for this exact version and came back still mismatched,
          // so reloading again cannot help. Stop rather than loop.
          Logger.error(
            'RefreshPageChecker::check',
            `Already reloaded for version ${data.version} but the mismatch persists; not reloading again`
          );
          return;
        }

        const isTvMode =
          window.location.hash.includes('tv-mode') ||
          window.location.pathname.includes('tv-mode');

        if (isTvMode) {
          // In TV mode, refresh directly without showing banner
          Logger.debug('RefreshPageChecker::check', 'In TV mode, refreshing');
          reloadForVersion(data.version);
          return;
        }

        // Not in TV mode, show the refresh banner
        setPendingVersion(data.version);
        setShowRefresh(true);
        setCountdown(COUNTDOWN_SECONDS);
      }
    } catch (error) {
      Logger.error('RefreshPageChecker::check', error);
    }
  };

  const onRefresh = () => {
    if (pendingVersion) {
      reloadForVersion(pendingVersion);
      return;
    }
    window.location.reload();
  };

  useEffect(() => {
    if (CHECK_FOR_REFRESH_INTERVAL) {
      check();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useInterval(() => check(), CHECK_FOR_REFRESH_INTERVAL);

  useEffect(() => {
    if (!showRefresh) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          Logger.debug('RefreshPageChecker::check', 'Time out, refreshing');
          onRefresh();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [showRefresh, pendingVersion]);

  if (!showRefresh) return null;

  if (wasDismissed)
    return (
      <div className={styles.dismissedMessage} onClick={onRefresh}>
        <Translate value="refresh_page_dismissed_message" />
      </div>
    );

  return (
    <div className={cn(styles.root, props.classNames)}>
      <div className={styles.content}>
        <InfoIcon className={styles.infoIcon} />
        <div className={styles.message}>
          {I18n.t('refresh_page_new_version')}
          <span className={styles.countdown}> {formatTime(countdown)}</span>
        </div>
        <div className={styles.refreshButton} onClick={onRefresh}>
          <Translate value="refresh_page_action" />
        </div>
      </div>
    </div>
  );
}

export default RefreshPageChecker;
